/**
 * Pre-sedation information letter — PDF component and field definitions.
 *
 * Auto-filled: clinic header, owner first name, patient name, owner phone.
 * Manual fields: arrival time, appointment date.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import {
  formatArrivalTime,
  formatCalendarDate,
  resolveDateFormat,
} from '@/frontend/features/printing/documents/blocks/format-utils'
import { InlineField } from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

export interface PreSedationLetterFields {
  arrivalTime: string
  appointmentDate: Date | null
}

export const defaultPreSedationLetterFields: PreSedationLetterFields = {
  arrivalTime: '',
  appointmentDate: null,
}

export { formatArrivalTime } from '@/frontend/features/printing/documents/blocks/format-utils'

export function introText(patientName: string): string {
  return `We have ${patientName} booked in to have a sedation with us. To ensure everything goes smoothly, here's some key information for your appointment. Please read through carefully, and if you have any questions, don't hesitate to reach out.`
}

export const ARRIVAL_NOTE =
  'This is 15 minutes before your scheduled appointment slot to allow you to sign and read the consent form.'

export function fastingIntro(patientName: string): string {
  return `${patientName} should be fasted for at least 6 hours before the appointment`
}

export const FASTING_INSTRUCTIONS = [
  'For morning procedures: No food after 11 PM the night before and no breakfast on the morning of the procedure.',
  'For afternoon procedures (1pm onwards): Give breakfast before 7am and then no food afterwards.',
  'Water is fine until the time of the procedure.',
]

export const ON_THE_DAY = [
  'Arrival time: Please arrive around 15 minutes before your scheduled appointment in order to allow you time to go through the consent form and ask any questions you may have.',
]

export function contactInfoText(ownerPhone: string): string {
  return `We currently have ${ownerPhone} as your contact number, please check this is correct.`
}

export function pickUpText(patientName: string): string {
  return `The sedation usually takes around 1 hour, you are welcome to wait for ${patientName} in reception or you can go away and we will call you when they're ready to pick up.`
}

export function afterProcedureText(patientName: string): string {
  return `After the procedure ${patientName} may be a little drowsy, but they can eat & drink as normal.`
}

export function shavedAreaText(patientName: string): string {
  return `${patientName} may have a shaved area on their front or back leg where an intravenous catheter was placed If you have questions or concerns, we're here to help.`
}

export const COST_NOTE =
  '**Please note that the cost of a sedation at Snoots is £60 flat fee for all pets & any medication required to go home will be additional.**'

export function closingText(patientName: string): string {
  return `Thank you again for trusting Snoots with ${patientName}'s care. We're looking forward to seeing you!`
}

const styles = StyleSheet.create({
  ...baseStyles,
})

export function PreSedationLetterPdf({
  context,
  fields,
}: {
  context: DocumentContext
  fields: PreSedationLetterFields
}) {
  const { patient, owner, clinic } = context
  // Generated for the owner, not a viewer, so the date order follows their region.
  const fmt = resolveDateFormat(null, owner.region)
  const arrivalTime = fields.arrivalTime ? formatArrivalTime(fields.arrivalTime) : undefined
  // A day picked in a date picker, not a moment — no zone may shift it.
  const appointmentDate = fields.appointmentDate
    ? formatCalendarDate(fields.appointmentDate, fmt, 'long')
    : undefined

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        <Text style={styles.text}>Dear {owner.firstName},</Text>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{introText(patient.name)}</Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={{ ...styles.text, ...styles.bold }}>
            The procedure will take place at {clinic.address}.
          </Text>
        </View>

        <View
          style={{
            ...styles.sectionGap,
            flexDirection: 'row',
            alignItems: 'baseline',
            gap: mm(2),
            flexWrap: 'wrap',
          }}
        >
          <Text style={styles.bold}>Please arrive at</Text>
          <InlineField value={arrivalTime} minWidth={mm(20)} />
          <Text style={styles.bold}>on the</Text>
          <InlineField value={appointmentDate} minWidth={mm(35)} />
          <Text style={styles.bold}>for your appointment.</Text>
        </View>
        <Text style={{ ...styles.text, ...styles.bold }}>{ARRIVAL_NOTE}</Text>

        <Text style={styles.heading}>What You Need to Know:</Text>

        <Text style={{ ...styles.heading, fontSize: 10 }}>1. Fasting Instructions</Text>
        <Text style={styles.listItem}>• {fastingIntro(patient.name)}</Text>
        {FASTING_INSTRUCTIONS.map((item) => (
          <Text key={item.slice(0, 30)} style={styles.listItem}>
            • {item}
          </Text>
        ))}

        <Text style={{ ...styles.heading, fontSize: 10 }}>2. On the day</Text>
        <Text style={styles.listItem}>
          • <Text style={styles.bold}>Arrival time:</Text> Please arrive around 15 minutes before
          your scheduled appointment in order to allow you time to go through the consent form and
          ask any questions you may have.
        </Text>
        <Text style={styles.listItem}>
          • <Text style={styles.bold}>Contact Info:</Text> {contactInfoText(owner.phone)}
        </Text>

        <Text style={{ ...styles.heading, fontSize: 10 }}>5. Post-Op Care</Text>
        <Text style={styles.listItem}>
          • <Text style={styles.bold}>Pick-Up Time:</Text> {pickUpText(patient.name)}
        </Text>
        <Text style={styles.listItem}>• {afterProcedureText(patient.name)}</Text>
        <Text style={styles.listItem}>• {shavedAreaText(patient.name)}</Text>

        <View style={styles.sectionGap}>
          <Text style={{ ...styles.text, ...styles.bold }}>{COST_NOTE}</Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{closingText(patient.name)}</Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>Best wishes,</Text>
          <Text style={styles.text}>The Snoots Team, North London</Text>
        </View>
      </Page>
    </Document>
  )
}
